import { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../axiosSetup';
import CustomNavbar from '../CustomNavbar';
import cfg from '../../config';

const formatDt = (dt) =>
  dt ? new Date(dt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '–';

const isExpired = (deadline) => new Date(deadline) < new Date();

const EMPTY_FORM = {
  title: '', description: '', deadline: '',
  isAnonymous: false, isMultiChoice: false, forZunftrat: false,
  sendPush: true, // beim Anlegen vorausgewählt; beim Bearbeiten wird nie gesendet
  options: [{ label: '' }, { label: '' }],
};

// ── Stil-Konstanten ──────────────────────────────────────────
const S = {
  card:       { background: '#1e2130', border: '1px solid #2e3347', borderRadius: 10 },
  cardHeader: { background: '#252839', borderBottom: '1px solid #2e3347', padding: '0.85rem 1.2rem', borderRadius: '10px 10px 0 0' },
  input:      { background: '#13151f', border: '1px solid #3a3f55', color: '#e8eaf0', borderRadius: 6 },
  label:      { color: '#c8cfe0', fontWeight: 600, fontSize: '0.88rem', marginBottom: 4 },
  muted:      { color: '#7b82a0', fontSize: '0.82rem' },
  badge: {
    expired:   { background: '#3a3f55', color: '#9aa3bc', fontSize: '0.72rem', padding: '2px 8px', borderRadius: 20 },
    zunftrat:  { background: '#1a4a7a', color: '#7ec8ff', fontSize: '0.72rem', padding: '2px 8px', borderRadius: 20 },
    anonymous: { background: '#2a4a2a', color: '#7eda7e', fontSize: '0.72rem', padding: '2px 8px', borderRadius: 20 },
    multi:     { background: '#4a3a1a', color: '#ffc96b', fontSize: '0.72rem', padding: '2px 8px', borderRadius: 20 },
  },
};

// ── Ergebnis-Modal ───────────────────────────────────────────
const ResultModal = ({ pollId, onClose }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [tab, setTab]         = useState('overview');   // 'overview' | 'voters'
  const [search, setSearch]   = useState('');

  const load = useCallback(async () => {
    try {
      setData((await api.get(`/api/polls/${pollId}/votes`)).data);
    } catch { setError('Fehler beim Laden.'); }
    finally { setLoading(false); }
  }, [pollId]);

  useEffect(() => { load(); }, [load]);

  const formatTs = (ts) => ts ? new Date(ts).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '–';

  // Alle Wähler aggregiert: { memberID, name, votedAt, optionLabels[] }
  const allVoters = useMemo(() => {
    if (!data || data.poll.isAnonymous) return [];
    const map = new Map();
    data.options.forEach(opt => {
      (opt.voters || []).forEach(v => {
        if (!map.has(v.memberID)) map.set(v.memberID, { ...v, optionLabels: [] });
        map.get(v.memberID).optionLabels.push(opt.label);
      });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [data]);

  const filteredVoters = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allVoters;
    return allVoters.filter(v =>
      v.name.toLowerCase().includes(q) ||
      v.optionLabels.some(l => l.toLowerCase().includes(q))
    );
  }, [allVoters, search]);

  const tabStyle = (key) => ({
    background: tab === key ? '#ffc107' : 'none',
    color:      tab === key ? '#000'    : '#7b82a0',
    border:     `1px solid ${tab === key ? 'transparent' : '#2e3347'}`,
    borderRadius: 20, padding: '4px 14px', cursor: 'pointer',
    fontWeight: tab === key ? 600 : 400, fontSize: '0.85rem',
    transition: 'all 0.15s',
  });

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 12px 40px', overflowY: 'auto' }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: '#1e2130', border: '1px solid #2e3347', borderRadius: 12, width: '100%', maxWidth: 700, display: 'flex', flexDirection: 'column' }}>

        {/* ── Header ── */}
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #2e3347', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
          <div>
            <div style={{ color: '#e8eaf0', fontWeight: 700, fontSize: '1rem' }}>
              {data ? data.poll.title : 'Ergebnisse'}
            </div>
            {data && (
              <div style={{ color: '#7b82a0', fontSize: '0.8rem', marginTop: 2 }}>
                {data.totalVotes} Stimme{data.totalVotes !== 1 ? 'n' : ''}
                {data.poll.isAnonymous ? ' · Anonym' : ` · ${allVoters.length} Teilnehmer`}
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#7b82a0', fontSize: 22, cursor: 'pointer', lineHeight: 1, marginLeft: 12, flexShrink: 0 }}>×</button>
        </div>

        {/* ── Tabs (nur wenn nicht anonym) ── */}
        {data && !data.poll.isAnonymous && (
          <div style={{ padding: '10px 1.25rem', borderBottom: '1px solid #2e3347', display: 'flex', gap: 8, flexShrink: 0 }}>
            <button style={tabStyle('overview')} onClick={() => setTab('overview')}>Übersicht</button>
            <button style={tabStyle('voters')} onClick={() => setTab('voters')}>
              Teilnehmer ({allVoters.length})
            </button>
          </div>
        )}

        {/* ── Body ── */}
        <div style={{ overflowY: 'auto', padding: '1.25rem', maxHeight: '65vh' }}>
          {loading && <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner-border" style={{ color: '#ffc107' }} /></div>}
          {error   && <div className="alert alert-danger">{error}</div>}

          {/* ── Übersicht-Tab: Balken ── */}
          {data && (tab === 'overview' || !!data.poll.isAnonymous) && (
            <>
              {data.options.map(opt => {
                const pct = data.totalVotes > 0 ? Math.round((opt.voteCount / data.totalVotes) * 100) : 0;
                return (
                  <div key={opt.id} style={{ marginBottom: 18 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ color: '#e8eaf0', fontWeight: 600, fontSize: '0.92rem', marginRight: 8 }}>{opt.label}</span>
                      <span style={{ color: '#7b82a0', fontSize: '0.82rem', flexShrink: 0 }}>
                        {opt.voteCount} ({pct}%)
                      </span>
                    </div>
                    <div style={{ background: '#13151f', borderRadius: 6, height: 10 }}>
                      <div style={{ height: '100%', borderRadius: 6, width: `${pct}%`, background: pct > 0 ? '#ffc107' : '#2e3347', transition: 'width 0.5s ease' }} />
                    </div>
                  </div>
                );
              })}
              {!!data.poll.isAnonymous && (
                <p style={{ color: '#4a5068', fontSize: '0.82rem', fontStyle: 'italic', marginTop: 8, marginBottom: 0 }}>
                  Anonym – Einzelstimmen werden nicht gespeichert.
                </p>
              )}
            </>
          )}

          {/* ── Teilnehmer-Tab: durchsuchbare Liste ── */}
          {data && !data.poll.isAnonymous && tab === 'voters' && (
            <>
              {/* Suche */}
              <div style={{ position: 'relative', marginBottom: 14 }}>
                <input
                  type="text"
                  placeholder="Name oder Option suchen…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{
                    width: '100%', background: '#13151f', border: '1px solid #3a3f55',
                    borderRadius: 8, color: '#e8eaf0', padding: '8px 36px 8px 12px',
                    fontSize: '0.9rem', outline: 'none',
                  }}
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#7b82a0', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                  >×</button>
                )}
              </div>

              {/* Zähler */}
              <div style={{ color: '#7b82a0', fontSize: '0.8rem', marginBottom: 10 }}>
                {filteredVoters.length === allVoters.length
                  ? `${allVoters.length} Teilnehmer gesamt`
                  : `${filteredVoters.length} von ${allVoters.length} Teilnehmern`}
              </div>

              {filteredVoters.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#4a5068', padding: '30px 0', fontSize: '0.9rem' }}>
                  Keine Treffer für „{search}"
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {filteredVoters.map(v => (
                    <div
                      key={v.memberID}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 10,
                        background: '#13151f', border: '1px solid #252839',
                        borderRadius: 8, padding: '9px 12px',
                      }}
                    >
                      {/* Name + Zeitpunkt */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: '#e8eaf0', fontWeight: 600, fontSize: '0.9rem', marginBottom: 3 }}>
                          {v.name}
                        </div>
                        <div style={{ color: '#7b82a0', fontSize: '0.75rem' }}>
                          {formatTs(v.votedAt)}
                        </div>
                      </div>
                      {/* Gewählte Option(en) */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end', flexShrink: 0, maxWidth: '55%' }}>
                        {v.optionLabels.map((label, i) => (
                          <span
                            key={i}
                            style={{
                              background: '#1a2a3a', border: '1px solid #ffc10744',
                              color: '#ffc107', borderRadius: 20,
                              padding: '2px 9px', fontSize: '0.75rem', fontWeight: 500,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {label}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ZRPolls = () => {
  const [polls, setPolls]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [success, setSuccess]     = useState(null);
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [deleteId, setDeleteId]   = useState(null);
  const [resultId, setResultId]   = useState(null); // Modal: welche Abstimmung anzeigen

  const load = async () => {
    setLoading(true);
    try { setPolls((await api.get('/api/polls')).data || []); }
    catch { setError('Fehler beim Laden.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setShowForm(true); setError(null); };

  const openEdit = async (poll) => {
    if (poll.totalVotes > 0) { setError('Bearbeitung gesperrt – es liegen bereits Stimmen vor.'); return; }
    try {
      const p = (await api.get(`/api/polls/${poll.id}`)).data;
      setForm({
        title: p.title, description: p.description || '', deadline: p.deadline?.slice(0, 16) || '',
        isAnonymous: !!p.isAnonymous, isMultiChoice: !!p.isMultiChoice, forZunftrat: !!p.forZunftrat,
        sendPush: false,
        options: p.options?.length ? p.options.map(o => ({ label: o.label })) : [{ label: '' }, { label: '' }],
      });
      setEditing(poll.id); setShowForm(true); setError(null);
    } catch { setError('Fehler beim Laden der Abstimmung.'); }
  };

  const handleField = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const updateOption = (i, value) =>
    setForm(prev => { const o = [...prev.options]; o[i] = { label: value }; return { ...prev, options: o }; });
  const addOption = () => form.options.length < 20 &&
    setForm(prev => ({ ...prev, options: [...prev.options, { label: '' }] }));
  const removeOption = (i) => form.options.length > 2 &&
    setForm(prev => ({ ...prev, options: prev.options.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const filled = form.options.filter(o => o.label.trim());
    if (filled.length < 2) { setError('Mindestens 2 ausgefüllte Optionen erforderlich.'); return; }
    setSaving(true);
    try {
      const payload = { ...form, options: filled };
      if (editing) { await api.put(`/api/polls/${editing}`, payload); setSuccess('Abstimmung aktualisiert.'); }
      else          { await api.post('/api/polls', payload);           setSuccess(form.sendPush ? 'Abstimmung erstellt, Mitglieder wurden benachrichtigt.' : 'Abstimmung erstellt (ohne Benachrichtigung).'); }
      setShowForm(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Fehler beim Speichern.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/api/polls/${deleteId}`); setSuccess('Abstimmung gelöscht.'); setDeleteId(null); load(); }
    catch (err) { setError(err.response?.data?.message || 'Fehler beim Löschen.'); setDeleteId(null); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0f1117' }}>
      <CustomNavbar />
      <div className="container mt-4 pb-5" style={{ maxWidth: 860 }}>

        {/* ── Header ── */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 style={{ color: '#e8eaf0', fontWeight: 700, marginBottom: 2 }}>Abstimmungen verwalten</h2>
            <span style={S.muted}>Erstellen, bearbeiten und auswerten</span>
          </div>
          <button
            className="btn btn-primary"
            onClick={openCreate}
            style={{ borderRadius: 8, fontWeight: 600, padding: '8px 18px' }}
          >
            + Neue Abstimmung
          </button>
        </div>

        {error   && <div className="alert alert-danger d-flex justify-content-between align-items-center py-2">{error}<button className="btn-close btn-close-white btn-sm" onClick={() => setError(null)} /></div>}
        {success && <div className="alert alert-success d-flex justify-content-between align-items-center py-2">{success}<button className="btn-close btn-close-white btn-sm" onClick={() => setSuccess(null)} /></div>}

        {/* ── Formular ── */}
        {showForm && (
          <div style={{ ...S.card, marginBottom: 24 }}>
            <div style={S.cardHeader} className="d-flex justify-content-between align-items-center">
              <span style={{ color: '#e8eaf0', fontWeight: 600 }}>
                {editing ? 'Abstimmung bearbeiten' : 'Neue Abstimmung'}
              </span>
              <button
                onClick={() => setShowForm(false)}
                style={{ background: 'none', border: 'none', color: '#7b82a0', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
              >×</button>
            </div>
            <div style={{ padding: '1.25rem' }}>
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label style={S.label}>Titel <span style={{ color: '#f87171' }}>*</span></label>
                  <input className="form-control" style={S.input} name="title" value={form.title} onChange={handleField} maxLength={255} required placeholder="Worum geht es bei dieser Abstimmung?" />
                </div>
                <div className="mb-3">
                  <label style={S.label}>Beschreibung <span style={S.muted}>(optional)</span></label>
                  <textarea className="form-control" style={S.input} name="description" value={form.description} onChange={handleField} rows={3} placeholder="Hintergründe oder Hinweise für die Abstimmenden…" />
                </div>
                <div className="mb-3">
                  <label style={S.label}>Abstimmungsfrist <span style={{ color: '#f87171' }}>*</span></label>
                  <input type="datetime-local" className="form-control" style={S.input} name="deadline" value={form.deadline} onChange={handleField} required />
                </div>

                <div className="mb-3 d-flex flex-wrap gap-4">
                  {[
                    { id: 'isAnonymous',  label: 'Anonym (keine Namen in Ergebnissen)' },
                    { id: 'isMultiChoice', label: 'Mehrfachauswahl erlaubt' },
                    { id: 'forZunftrat',  label: `Nur ${cfg.adminRoleName} darf abstimmen` },
                    // Push gibt es nur beim Anlegen — eine Änderung löst keine aus
                    ...(editing ? [] : [{ id: 'sendPush', label: `🔔 ${cfg.memberTermPlural} per Push benachrichtigen` }]),
                  ].map(({ id, label }) => (
                    <div key={id} className="form-check d-flex align-items-center gap-2 m-0">
                      <input type="checkbox" className="form-check-input" style={{ width: 16, height: 16, cursor: 'pointer' }}
                        id={id} name={id} checked={form[id]} onChange={handleField} />
                      <label className="form-check-label" htmlFor={id} style={{ color: '#c8cfe0', cursor: 'pointer', margin: 0 }}>{label}</label>
                    </div>
                  ))}
                </div>

                <label style={S.label}>Antwortoptionen <span style={{ color: '#f87171' }}>*</span></label>
                <div className="mb-1">
                  {form.options.map((o, i) => (
                    <div key={i} className="d-flex gap-2 mb-2">
                      <input
                        className="form-control" style={S.input}
                        placeholder={`Option ${i + 1}`}
                        value={o.label}
                        onChange={e => updateOption(i, e.target.value)}
                        maxLength={255}
                      />
                      {form.options.length > 2 && (
                        <button type="button" onClick={() => removeOption(i)}
                          style={{ background: 'none', border: '1px solid #5a2020', color: '#f87171', borderRadius: 6, padding: '0 10px', cursor: 'pointer' }}>
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {form.options.length < 20 && (
                  <button type="button" onClick={addOption}
                    style={{ background: 'none', border: '1px dashed #3a3f55', color: '#7b82a0', borderRadius: 6, padding: '5px 14px', cursor: 'pointer', fontSize: '0.85rem', marginBottom: 16 }}>
                    + Option hinzufügen
                  </button>
                )}

                <div className="d-flex gap-2 mt-3">
                  <button type="submit" className="btn btn-primary" disabled={saving} style={{ borderRadius: 7, fontWeight: 600 }}>
                    {saving ? 'Speichern…' : 'Speichern'}
                  </button>
                  <button type="button" onClick={() => setShowForm(false)}
                    style={{ background: 'none', border: '1px solid #3a3f55', color: '#9aa3bc', borderRadius: 7, padding: '6px 16px', cursor: 'pointer' }}>
                    Abbrechen
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── Liste ── */}
        {loading ? (
          <div className="text-center py-5"><div className="spinner-border" style={{ color: '#ffc107' }} /></div>
        ) : polls.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#4a5068', padding: '60px 0' }}>
            Noch keine Abstimmungen vorhanden.
          </div>
        ) : (
          polls.map(poll => {
            const expired = isExpired(poll.deadline);
            return (
              <div key={poll.id} style={{ ...S.card, marginBottom: 12 }}>
                <div style={{ padding: '1rem 1.2rem' }}>
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">

                    {/* ── Linke Seite: Infos ── */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                        <span style={{ color: '#e8eaf0', fontWeight: 600, fontSize: '1rem' }}>{poll.title}</span>
                        {expired      && <span style={S.badge.expired}>Abgelaufen</span>}
                        {!!poll.forZunftrat  && <span style={S.badge.zunftrat}>Nur {cfg.adminRoleName}</span>}
                        {!!poll.isAnonymous  && <span style={S.badge.anonymous}>Anonym</span>}
                        {!!poll.isMultiChoice && <span style={S.badge.multi}>Mehrfachauswahl</span>}
                      </div>
                      {poll.description && (
                        <p style={{ ...S.muted, marginBottom: 4 }}>{poll.description}</p>
                      )}
                      <span style={S.muted}>
                        Frist: {formatDt(poll.deadline)}
                        &ensp;·&ensp;{poll.totalVotes} Stimme{poll.totalVotes !== 1 ? 'n' : ''}
                        &ensp;·&ensp;{poll.optionCount} Optionen
                      </span>
                    </div>

                    {/* ── Rechte Seite: Aktionen ── */}
                    <div className="d-flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => setResultId(poll.id)}
                        style={{ background: 'none', border: '1px solid #3a3f55', color: '#9aa3bc', borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontSize: '0.85rem' }}
                      >
                        Ergebnisse
                      </button>
                      <button
                        onClick={() => openEdit(poll)}
                        disabled={poll.totalVotes > 0}
                        title={poll.totalVotes > 0 ? 'Gesperrt – Stimmen vorhanden' : 'Bearbeiten'}
                        style={{
                          background: 'none', border: '1px solid #1a4a8a', color: poll.totalVotes > 0 ? '#3a4a6a' : '#5b9cf6',
                          borderRadius: 7, padding: '5px 12px', cursor: poll.totalVotes > 0 ? 'not-allowed' : 'pointer', fontSize: '0.85rem',
                        }}
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => setDeleteId(poll.id)}
                        style={{ background: 'none', border: '1px solid #5a2020', color: '#f87171', borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontSize: '0.85rem' }}
                      >
                        Löschen
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}

        {/* ── Ergebnis-Modal ── */}
        {resultId && <ResultModal pollId={resultId} onClose={() => setResultId(null)} />}

        {/* ── Löschen-Bestätigung ── */}
        {deleteId && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ ...S.card, maxWidth: 400, width: '90%', padding: '1.5rem', textAlign: 'center' }}>
              <h5 style={{ color: '#e8eaf0', marginBottom: 8 }}>Abstimmung löschen?</h5>
              <p style={S.muted}>Alle Stimmen und Optionen werden unwiderruflich gelöscht.</p>
              <div className="d-flex gap-3 justify-content-center mt-3">
                <button onClick={() => setDeleteId(null)}
                  style={{ background: 'none', border: '1px solid #3a3f55', color: '#9aa3bc', borderRadius: 7, padding: '7px 20px', cursor: 'pointer' }}>
                  Abbrechen
                </button>
                <button onClick={handleDelete} className="btn btn-danger" style={{ borderRadius: 7 }}>
                  Löschen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ZRPolls;
